/**
 * crucible#3187 — the shared voice/text steering composer ROW (mic button +
 * textarea + send button), extracted from `SoulInterviewTurnCard`'s moodboard
 * turn's "Lock this look" phone branch (the interview's own build-time asset-
 * steering precedent — see that component's docblock note: "mirrors the
 * build-time asset-review phone driver's visual language... it's all
 * potential consumer surface so it should look unified").
 *
 * This extracts the PATTERN + exact CSS treatment, not a monolithic
 * component: it owns ONLY the composer row (non-terminal "steer" input).
 * Callers own their own voice-recognition hook (they differ — the interview
 * card's hand-rolled `useState` version vs `TvSteeringSurface`'s
 * `useSpeechFeedback` hook — unifying THOSE is out of scope here) and their
 * own terminal action (a "Lock this look" / "Keep it" button lives OUTSIDE
 * this component, styled with the same `glint-halo brand-gradient-fill`
 * treatment at each call site).
 *
 * Used by:
 *   - `SoulInterviewTurnCard.tsx`'s moodboard phone branch.
 *   - `PhoneSteeringController.tsx` (the new #3187 carousel phone controller).
 */
import type { KeyboardEvent } from 'react';

export interface SteerComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Disables the textarea + send button. Send additionally requires
   *  non-empty trimmed text (computed internally — callers don't need to
   *  fold that into `disabled` themselves). */
  disabled: boolean;
  /** Disables the mic button specifically. Defaults to `disabled` when
   *  omitted — pass this explicitly when the mic has an ADDITIONAL gate
   *  the textarea doesn't (e.g. "voice unsupported in this browser"). */
  micDisabled?: boolean;
  placeholder: string;
  textareaAriaLabel: string;
  micTitle: string;
  micAriaLabel: string;
  sendAriaLabel: string;
  listening: boolean;
  onMicToggle: () => void;
  /** Textarea row count. Both existing call sites use 2. */
  rows?: number;
  testIds: {
    /** The input-shell wrapper div. Optional — neither existing call site
     *  currently asserts on it directly by a NEW id (SoulInterviewTurnCard's
     *  existing `soul-interview-phone-input-shell` id is preserved here). */
    shell?: string;
    micButton: string;
    input: string;
    send: string;
  };
}

export function SteerComposer({
  value,
  onChange,
  onSubmit,
  disabled,
  micDisabled,
  placeholder,
  textareaAriaLabel,
  micTitle,
  micAriaLabel,
  sendAriaLabel,
  listening,
  onMicToggle,
  rows = 2,
  testIds,
}: SteerComposerProps): React.JSX.Element {
  const sendDisabled = disabled || value.trim().length === 0;
  const micIsDisabled = micDisabled ?? disabled;

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    event.preventDefault();
    if (!sendDisabled) onSubmit();
  };

  return (
    <div
      className="flex items-end gap-2 rounded-2xl border border-white/12 bg-black/55 p-2 focus-within:border-crucible-accent/75"
      data-testid={testIds.shell}
    >
      <button
        type="button"
        title={micTitle}
        aria-label={micAriaLabel}
        aria-pressed={listening}
        data-testid={testIds.micButton}
        onClick={onMicToggle}
        disabled={micIsDisabled}
        className={[
          'grid h-11 w-11 shrink-0 place-items-center rounded-full border transition',
          listening
            ? 'border-crucible-accent bg-crucible-accent text-black'
            : 'border-white/14 text-white/70 hover:border-white/26',
          micIsDisabled ? 'cursor-not-allowed opacity-45' : '',
        ].join(' ')}
      >
        <MicIcon size={20} />
      </button>
      <textarea
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={rows}
        placeholder={placeholder}
        aria-label={textareaAriaLabel}
        aria-keyshortcuts="Enter"
        data-testid={testIds.input}
        className="min-h-[46px] flex-1 resize-none bg-transparent px-1.5 py-1.5 text-[15px] leading-snug text-white/92 placeholder:text-white/32 focus:outline-none disabled:opacity-45"
      />
      <button
        type="button"
        data-testid={testIds.send}
        aria-label={sendAriaLabel}
        disabled={sendDisabled}
        onClick={onSubmit}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-crucible-accent/35 text-crucible-accent transition hover:border-crucible-accent hover:bg-crucible-accent hover:text-black disabled:border-white/10 disabled:text-white/24 disabled:hover:bg-transparent disabled:hover:text-white/24"
      >
        <ArrowIcon />
      </button>
    </div>
  );
}

export function ArrowIcon(): React.JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function MicIcon({ size = 15 }: { size?: number } = {}): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
      <path d="M12 18v4" />
      <path d="M8 22h8" />
    </svg>
  );
}
